import moment from "moment";
import jwtDecode from "jwt-decode";

// utils
import { paths } from "src/routes/paths";
import axios from "./axios";
import { refreshToken as refresh, refreshAccessToken } from "../api/user";
import history from "./history";
import enqueueSnackbar from "./notify";
import i18n from "../locales/i18n";
import { clearCachedLabels } from "../locales/labels-cache";
import { errorCodeToCamelCase } from "./error";
import { deleteMessagingDeviceToken } from "./fcm-token";
import { clearIdentityProviderSession } from "./identity-provider";

// redux
import { dispatch } from "../redux/store";
import { setAuthenticated } from "../redux/slices/userProvider";

// Number of consecutive failed refresh attempts against an unhealthy backend
// before we give up and expire the session.
export const MAX_REFRESH_ATTEMPTS = 5;

// Backend error codes whose own copy explains the logout better than the generic "session
// expired". Codes like User.InvalidCredentials are deliberately absent: their wording is wrong
// for a session that simply became unusable.
const SURFACEABLE_ERROR_CODES = ["User.EnterpriseNotFound", "User.NoEnterpriseAvailable"];

// Marks the backend as unhealthy and parks the user on the maintenance page, keeping the
// session intact so the retry loop there can recover it. Remembers where they came from so a
// successful refresh can send them back.
export const enterMaintenance = () => {
  window.localStorage.setItem("hasLoginProblem", true);

  if (window.location.pathname !== paths.maintenance) {
    window.localStorage.setItem("returnPath", window.location.pathname);
    history.push(paths.maintenance);
  }
};

// Tears down the session completely and clears every login-problem marker.
export const expireSession = () => {
  /*
    Revoking the FCM token is what stops push notifications reaching a device whose session is
    gone: the push is delivered to the browser, not to the app, so an involuntary expiry here
    (a failed refresh, a dead refresh token, too many retries) would otherwise leave Chrome
    showing notifications for a user who is logged out.

    Fire and forget: this runs from synchronous paths (the axios interceptor, the auth provider)
    that cannot await, and deleteMessagingDeviceToken() resolves rather than rejects, so there is
    no unhandled rejection to guard against. The matching user_device_token row is only removed
    when the teardown came from an explicit logout, which posts the token to POST api/users/logout
    first; otherwise the backend prunes it once FCM answers UNREGISTERED.
  */
  deleteMessagingDeviceToken();

  setSession(null, null, false);
  // An expired session only drops the IdP data: ending the IdP session is an explicit logout
  clearIdentityProviderSession();
  clearCachedLabels();
  dispatch(setAuthenticated({ authenticated: false, user: null }));
  window.localStorage.setItem("refreshAttempt", 0);
  window.localStorage.setItem("hasLoginProblem", false);
  window.localStorage.removeItem("returnPath");
};

export const isValidToken = accessToken => {
  if (!accessToken) {
    return false;
  }

  const decodedToken = jwtDecode(accessToken);
  const currentTime = Date.now() / 1000;
  return decodedToken.exp > currentTime;
};

export const setSession = (accessToken, refreshToken, rememberMe) => {
  if (accessToken) {
    window.localStorage.setItem("accessToken", accessToken);
    window.localStorage.setItem("refreshToken", refreshToken ?? "");
    window.localStorage.setItem("rememberMe", rememberMe);
    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
  } else {
    window.localStorage.removeItem("accessToken");
    window.localStorage.removeItem("refreshToken");
    delete axios.defaults.headers.common.Authorization;
  }
};

export const refreshToken = async (redirect = false, isInitializing = false) => {
  return navigator.locks.request("refreshToken", async () => {
    const responseResult = { result: "error", data: null, errorCode: null };
    // Were we already stuck in a login-problem state before this attempt?
    const hadLoginProblem = JSON.parse(window.localStorage.getItem("hasLoginProblem") ?? "false");

    try {
      const accessToken = window.localStorage.getItem("accessToken");

      // No token at all -> the user is logged out (or never logged in). There is nothing to
      // refresh and no session to expire, so let the caller cancel the request silently.
      // The AuthGuard is responsible for redirecting unauthenticated users to login; we must
      // NOT show a "session expired" message here (e.g. for stray requests fired by components
      // that are still unmounting right after a deliberate logout).
      if (!accessToken) {
        responseResult.result = "notAuthenticated";
        return responseResult;
      }

      const decodedToken = jwtDecode(accessToken);
      const expirationCalculus = getExpirationCalculus(decodedToken);
      const refreshTokenValue = window.localStorage.getItem("refreshToken");
      const rememberMe = window.localStorage.getItem("rememberMe");
      const currentRefreshAttempt = parseInt(
        window.localStorage.getItem("refreshAttempt") ?? 0,
        10
      );

      // We have retried against an unhealthy backend too many times: finally
      // expire the session and send the user to login with feedback.
      if (currentRefreshAttempt >= MAX_REFRESH_ATTEMPTS) {
        expireSession();
        responseResult.result = "expiredToken";

        enqueueSnackbar(i18n.t("error:sessionExpired"), { variant: "warning" });
        history.push(paths.auth.login);

        return responseResult;
      }

      /*
        If the token is about to expire (10 minutes till expiration) or is already expired but
        there's a refresh token, then try to refresh the token.
      */
      if (expirationCalculus.aboutToExpire || (!expirationCalculus.isValid && refreshTokenValue)) {
        // Count the attempt BEFORE the network call so a failure is recorded.
        window.localStorage.setItem("refreshAttempt", currentRefreshAttempt + 1);

        const response = refreshTokenValue ? await refresh() : await refreshAccessToken();

        const { accessToken: newAccessToken, refreshToken: newRefreshToken, user } = response.data;

        setSession(newAccessToken, newRefreshToken, rememberMe);
        dispatch(setAuthenticated({ authenticated: true, user }));

        responseResult.data = response.data;
        responseResult.result = "success";
      } else if (expirationCalculus.isValid) {
        setSession(accessToken, refreshTokenValue, rememberMe);
        responseResult.result = "success";
      } else {
        expireSession();
        responseResult.result = "expiredToken";
        if (redirect) history.push(paths.auth.login);
      }
    } catch (e) {
      const status = e.response?.status;
      const errorType = e.response?.data?.type;

      /*
        Transient backend problem: the server is unreachable (e.g. a deploy / IIS restart)
        or it answered with an unexpected 5xx error. Keep the session intact so the user can
        recover, mark the login problem and route them to the maintenance page instead of
        logging them out. The maintenance page polls refreshToken until the backend recovers
        or MAX_REFRESH_ATTEMPTS is reached.

        Error.Unexpected is deliberately NOT transient: the command pipeline converts every
        handler exception into a 400 Error.Unexpected, so treating it as an outage parks the
        user on the maintenance page with a live but broken session. A refresh that cannot
        succeed must log them out instead.
      */
      const isTransient =
        Boolean(e?.request) && (!e.response || status >= 500 || errorType === "Error.Timeout");

      if (isTransient) {
        responseResult.result = "error";
        enterMaintenance();

        return responseResult;
      }

      // Genuine auth failure (e.g. invalid/expired refresh token, or an enterprise that no longer
      // resolves) -> expire the session.
      expireSession();
      responseResult.result = "expiredToken";
      responseResult.errorCode = SURFACEABLE_ERROR_CODES.includes(errorType) ? errorType : null;

      // Show the backend's own reason when it has one; otherwise the caller falls back to the
      // generic "session expired" message.
      if (responseResult.errorCode) {
        enqueueSnackbar(i18n.t(`error:${errorCodeToCamelCase(responseResult.errorCode)}`), {
          variant: "warning",
        });
      }

      if (redirect) history.push(paths.auth.login);

      return responseResult;
    }

    if (responseResult.result === "success") {
      window.localStorage.setItem("refreshAttempt", 0);

      // If we were stuck on the maintenance page, the backend just recovered:
      // clear the problem flag and send the user back where they came from.
      if (hadLoginProblem) {
        window.localStorage.setItem("hasLoginProblem", false);

        const returnPath = window.localStorage.getItem("returnPath") || "/";
        window.localStorage.removeItem("returnPath");

        if (!isInitializing && window.location.pathname === paths.maintenance) {
          history.push(returnPath);
        }
      }
    }

    return responseResult;
  });
};

function getExpirationCalculus(token) {
  const currentTime = moment();
  const validTo = moment(new Date(token.exp * 1000));

  const difference = validTo.diff(currentTime, "seconds");

  return {
    aboutToExpire: difference < 600, // 10 minutes
    isValid: token.exp > Date.now() / 1000,
  };
}
